import { Injectable } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { BehaviorSubject, Observable } from 'rxjs';
import { SupabaseService } from './supabase.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private supabase: SupabaseClient;
  private currentUserSubject = new BehaviorSubject<any>(null);
  public currentUser$: Observable<any> = this.currentUserSubject.asObservable();

  constructor(private supabaseService: SupabaseService) {
    // Reuse the single shared Supabase client so there is only one auth
    // (GoTrue) instance per browser tab. Creating a second client with the
    // same storageKey makes them contend for the "lock:sb-token" Web Lock,
    // which throws NavigatorLockAcquireTimeoutError repeatedly.
    this.supabase = this.supabaseService.client;
    this.loadUser();
  }

  private async loadUser() {
    // First check if we have a stored session
    const { data: { session } } = await this.supabase.auth.getSession();
    if (session) {
      this.currentUserSubject.next(session.user);
    } else {
      this.currentUserSubject.next(null);
    }

    // Set up listener for auth changes
    this.supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        this.currentUserSubject.next(session?.user || null);
      } else if (event === 'SIGNED_OUT') {
        this.currentUserSubject.next(null);
      }
    });
  }

  async signIn(email: string, password: string) {
    const { data, error } = await this.supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) throw error;
    return data;
  }

  async signUp(email: string, password: string) {
    const { data, error } = await this.supabase.auth.signUp({
      email,
      password
    });

    if (error) throw error;
    return data;
  }

  async signOut() {
    const { error } = await this.supabase.auth.signOut();
    if (error) throw error;
    this.currentUserSubject.next(null);
  }

  async changePassword(newPassword: string) {
    const { error } = await this.supabase.auth.updateUser({
      password: newPassword
    });

    if (error) throw error;
    return { success: true };
  }
  async isAuthenticatedAsync(): Promise<boolean> {
    // Check if we have an active session with Supabase
    const { data: { session } } = await this.supabase.auth.getSession();
    return session !== null;
  }

  isAuthenticated(): boolean {
    // Synchronous check from local storage - for route guards
    const token = localStorage.getItem('sb-token');
    return !!token;
  }

  getCurrentUser(): any {
    return this.currentUserSubject.getValue();
  }
}